<template>
    <ui-dropdown placement="bottom-start" :menu-class="menuClass" trigger-class="flex-1">
        <template #default="dropdownScope">
            <slot v-bind="scope(dropdownScope)"></slot>
        </template>

        <template #menu="{ close }">
            <div class="p-3 max-h-64 overflow-y-auto w-full">

                <!-- Empty State. -->
                <div v-if="!query" class="text-gray-500 text-sm">
                    Start typing to search for known labour rate...
                </div>
                <!-- Results -->
                <div v-else class="space-y-6">
                    <div v-if="labourRates.length > 0">
                        <div class="ml-2 text-xs font-medium text-gray-400 mb-1">Labour Rates</div>
                        <div class="space-y-1">
                            <div
                                v-for="labourRateGroup in groupedLabourRates"
                                class="flex items-center space-x-2 text-left w-full rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-600"
                                :title="labourRateGroup.groupName"
                            >
                                <div class="truncate flex-1 flex flex-col">
                                    <div class="truncate w-full">{{ labourRateGroup.groupName }}</div>
                                    <template v-for="lr in labourRateGroup.labourRates">
                                        <div class="truncate text-sms ml-3 mr-1 pt-0.5 p-0.5">
                                            <button
                                                @click="selectLabourRate(lr.name,lr.costPerUnit, close)"
                                                class="flex px-1.5 py-0.5 -my-0.5 border border-transparent rounded-lg text-xs w-full text-left font-medium hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-blue-500 hover:text-gray-600"
                                            >
                                                <span class="flex-1">{{
                                                        lr.name
                                                    }} &#x2022 {{ lr.costPerUnit | toCurrency }}<span
                                                        v-if="lr.unitOfMeasure"> per {{ lr.unitOfMeasure }}</span></span>
                                                <span v-if="lr.priceAgreement" class="ml-5 text-red-300"><span
                                                    class="fas fa-star"
                                                    style="font-size: 8px"> </span> Supplier Quote</span>
                                                <span v-else-if="lr.usedInEstimate" class="ml-5 text-blue-300"><span
                                                    class="fas fa-star" style="font-size: 8px"> </span> Used in Estimate</span>
                                            </button>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-gray-500 text-sm">
                        No matching labour rates found.
                    </div>
                </div>
            </div>
        </template>
    </ui-dropdown>
</template>

<script>

import {cloneDeep} from "lodash";
import {resolveNumericText} from "../../../../utils";

export default {
    props: {
        top: Boolean,
        buttonClass: {
            type: String,
            default: 'text-left w-full rounded-md px-2 py-1 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-600',
        },
        menuClass: {
            type: String,
            default: 'w-full',
        },
        store: Object,
        estimate: Object,
    },
    data() {
        return {
            query: '',
            labourRates: [],
            groupedLabourRates: [],
            resolveNumericText,
        }
    },
    computed: {},
    methods: {
        async searchLabourRates() {
            if (this.store.loaded) {
                if (!this.store.labourProcessed || (this.store.labourProcessed !== this.estimate.collectionBuildUUID)) {
                    let processedLabour = cloneDeep(this.store.labourRates);
                    this.store.supplierQuotes.forEach((sq) => {
                        sq.items.forEach((item) => {
                            if (item.type === 'Labour') {
                                let labour = processedLabour.find((i) => (
                                    (i.groupName === sq.supplierName) &&
                                    (i.name === item.name) &&
                                    (i.unitOfMeasure === item.unitOfMeasure) &&
                                    (i.costPerUnit === item.costPerUnit)
                                ));
                                if (!labour) {
                                    labour = {
                                        name: item.name,
                                        groupName: sq.supplierName,
                                        unitOfMeasure: item.unitOfMeasure,
                                        costPerUnit: item.costPerUnit
                                    }
                                    processedLabour.unshift(labour)
                                }
                                labour.priceAgreement = true;
                            }
                        })
                    })
                    Array.from(this.estimate.collectedItems.labour, ([name, value]) => value).forEach((i) => {
                        let itemRate = 0 + resolveNumericText(i.rate)
                        let labour = processedLabour.find((item) => item.name === i.name && item.costPerUnit === itemRate)
                        if (labour) {
                            processedLabour.splice(processedLabour.indexOf(labour), 1);
                            labour = {name: labour.name, groupName: labour.groupName, costPerUnit: labour.costPerUnit}
                        } else {
                            labour = {name: i.name, groupName: "Used in Estimate", costPerUnit: itemRate}
                        }
                        processedLabour.unshift(labour)
                        labour.usedInEstimate = true
                    })
                    this.store.labourProcessed = this.estimate.collectionBuildUUID;
                    this.store.processedLabour = processedLabour;
                }
                this.labourRates = this.store.processedLabour.filter(this.matchContact)
                    .sort((a, b) => {
                        let score = 0;
                        return score;
                    }).slice(0, 100);
                this.groupedLabourRates = []
                this.labourRates.forEach((l) => {
                    let option = this.groupedLabourRates.find((i) => i.groupName === l.groupName);
                    if (!option) {
                        option = {groupName: l.groupName, labourRates: []}
                        this.groupedLabourRates.push(option)
                    }
                    option.labourRates.push(l)
                })
            }
        },
        matchContact(labourRate) {
            const query = this.query.toLowerCase().split(/[^A-Za-z0-9.-]+/);
            return query.every((token) => {
                return (labourRate.name).toLowerCase().includes(token) || (labourRate.groupName).toLowerCase().includes(token)
            });
        },
        scope(dropdownScope) {
            return {
                query: this.query,
                setQuery: this.setQuery,
                ...dropdownScope
            }
        },
        setQuery(query) {
            this.query = query
            this.searchLabourRates();
        },
        selectLabourRate(labourRateName, labourRateCost, close) {
            this.$emit('selected:labourRate', {labourRateName, labourRateCost});
            this.query = labourRateName;
            close();
        },
    }
}
</script>
